// Discount math (v2.8.8a) — client mirror of server/utils/discountMath.js.
//
// ── EVERY FIGURE IS WHOLE CENTS, AND A DOCUMENT ADDS UP ────────────────────
//
// Changed 2026-09-14 alongside the server, by owner instruction: a bill's own
// figures must sum to its own total. Everything here is computed in INTEGER
// CENTS and returned as dollars, so `subtotal - discount + tax === total` is
// true by construction. discountParity.test.js runs both copies over 432
// cases and fails if they disagree by a cent — and this is not only a preview:
// PublicInvoice.js and PublicEstimate.js import `resolveDiscount` from here,
// so these are figures a CLIENT reads on a document.
//
// Exact IN WHOLE CENTS, which is what moneyAddsUp.test.js compares. The values
// RETURNED are `cents / 100` doubles, where the identity holds only up to
// floating-point representation — no displayed figure is wrong, because
// nothing is shown at that precision, but a future test comparing the dollar
// floats with `===` is where the difference would bite. Said here after review
// pointed out this header was slightly wider than the suite beneath it; that
// is the third time in this branch a claim outran its evidence, so it gets
// written down rather than trusted to be remembered.
//
// ONE EXCEPTION, named so the claim is not wider than its evidence: a
// NEGATIVE subtotal clamps — afterDiscount floors at 0 — so total is 0 while
// subtotal stays negative and the identity does not hold. That clamp predates
// this change and stays, because the alternative is a bill that owes the
// client money. It is unreachable through the API (every item validator is
// isFloat({ min: 0 })) and is asserted in moneyAddsUp.test.js rather than
// described, so removing it turns something red.
//
// Both files implement the same formula. The server is authoritative on
// save; this module powers the live preview total in the estimate and
// invoice forms so the user sees what the server will compute, before
// they hit Save. Keep in sync with server/utils/discountMath.js.
//
// Usage:
//   import { resolveDiscount, computeTotals } from '../../utils/discountMath';
//   const totals = computeTotals(items, form.discount_amount, form.discount_type, form.tax_rate);
//   // totals = { subtotal, discount, afterDiscount, taxAmount, total }

/**
 * Resolve a stored (amount, type) pair into actual dollars off, given the
 * current subtotal. Mirrors server resolveDiscount(...).
 *
 * @param {number} subtotal      pre-discount sum of line items
 * @param {number|string} amount stored value — dollars OR percent
 * @param {string} type          'fixed' | 'percent' (default: 'fixed')
 * @returns {number} dollar amount to subtract, never < 0 and never > subtotal
 */
export function resolveDiscount(subtotal, amount, type = 'fixed') {
  const sub = Number(subtotal) || 0;
  const raw = Number(amount) || 0;
  if (raw <= 0 || sub <= 0) return 0;

  let dollars;
  if (type === 'percent') {
    const pct = Math.max(0, Math.min(100, raw));
    dollars = sub * (pct / 100);
  } else {
    dollars = raw;
  }
  // Settled to whole cents here, at the one place a discount becomes a
  // figure. A percent of a subtotal is where the fraction comes from —
  // $100.03 at 10% is $10.003 — and displaying 10.00 while subtracting 10.003
  // is exactly how a bill stopped adding up.
  return toDollars(Math.min(toCents(sub), toCents(dollars)));
}

/** Dollars to whole cents, and back. Mirrors the server, same conversion. */
export function toCents(dollars) {
  const n = Number(dollars) || 0;
  return Number.isFinite(n) ? Math.round(n * 100) : n;
}

export function toDollars(cents) {
  return Number.isFinite(cents) ? cents / 100 : cents;
}

/**
 * Per-line breakdown (v2.9.37) — mirror of server computeLineItems(...).
 * Each item resolves its own discount against its gross (qty × unit_price).
 */
export function computeLineItems(items) {
  let subtotalC = 0;
  let lineDiscountC = 0;
  const lines = (items || []).map(item => {
    // Each line is settled to cents BEFORE it is added up, so the amounts a
    // person reads are the amounts that sum to the subtotal they read.
    const grossC = toCents((Number(item.quantity) || 0) * (Number(item.unit_price) || 0));
    const discountC = toCents(resolveDiscount(toDollars(grossC), item.discount_amount, item.discount_type));
    const netC = Math.max(0, grossC - discountC);
    subtotalC += grossC;
    lineDiscountC += discountC;
    return { gross: toDollars(grossC), discount: toDollars(discountC), net: toDollars(netC) };
  });
  return { lines, subtotal: toDollars(subtotalC), lineDiscountTotal: toDollars(lineDiscountC), };
}

/**
 * Recompute totals from items + discount + tax. Mirrors server
 * computeTotals(...). Returns numbers (not strings), already rounded to 2dp.
 *
 * v2.9.37: per-line-item discounts and a whole-invoice discount are mutually
 * exclusive — if ANY line carries a discount, the per-line discounts win and
 * the invoice-level one is ignored (matches the form gray-out). When no line
 * has a discount, behavior is identical to the pre-v2.9.37 formula.
 *
 * @param {Array<{quantity, unit_price, discount_amount?, discount_type?}>} items
 * @param {number} discountAmount  raw value (dollars or percent)
 * @param {string} discountType    'fixed' | 'percent'
 * @param {number} taxRatePercent  e.g. 8.25 for 8.25%
 * @returns {{subtotal, discount, afterDiscount, taxAmount, total,
 *            lineDiscountTotal, discountMode}}
 */
export function computeTotals(items, discountAmount, discountType, taxRatePercent) {
  const { subtotal, lineDiscountTotal } = computeLineItems(items);

  let discount;
  let discountMode;
  if (lineDiscountTotal > 0) {
    discount = lineDiscountTotal;
    discountMode = 'line';
  } else {
    discount = resolveDiscount(subtotal, discountAmount, discountType);
    discountMode = discount > 0 ? 'invoice' : 'none';
  }

  // The addition IS the definition. Whole cents throughout, so each figure is
  // the exact sum of the ones above it. `Math.max(0, …)` on the discount
  // because a negative quantity makes the subtotal negative, and clamping a
  // zero discount to a negative subtotal would ADD money to the bill.
  const subtotalC = toCents(subtotal);
  const discountC = Math.max(0, Math.min(subtotalC, toCents(discount)));
  const afterDiscountC = Math.max(0, subtotalC - discountC);
  const taxC = toCents(toDollars(afterDiscountC) * ((Number(taxRatePercent) || 0) / 100));
  const totalC = afterDiscountC + taxC;

  return {
    subtotal: toDollars(subtotalC),
    discount: toDollars(discountC),
    afterDiscount: toDollars(afterDiscountC),
    taxAmount: toDollars(taxC),
    total: toDollars(totalC),
    lineDiscountTotal: toDollars(toCents(lineDiscountTotal)),
    discountMode,
  };
}
